/**
 * Proposals list — INVOICED and PAID columns.
 *
 * The server (`backend/proposal_invoice_state.py`) resolves each row's
 * `invoice` object from the linked commercial record: the renewal cycle
 * a renewal proposal covers, or the FinOps draft an ad-hoc proposal was
 * converted into. This module only decides how that state READS. It
 * never derives an invoice from proposal status, and proposal status is
 * never derived from it: a proposal can be "Sent" while the renewal
 * cycle it covers has already been invoiced.
 */
import { toneClasses } from "./renewalLifecycle";

const PAYMENT_TONES = {
  awaiting_payment: "warning",
  part_paid: "warning",
  disputed: "danger",
  paid: "success",
  written_off: "muted",
};

// Ascending sort order for the PAID column: what still needs chasing first.
const PAYMENT_RANK = {
  awaiting_payment: 1,
  part_paid: 2,
  disputed: 3,
  paid: 4,
  written_off: 5,
};

/** "2026-09-21" -> "Sep 21, 2026". Date-only values are formatted as
 *  calendar dates — never pushed through a timezone, which could move
 *  them a day. */
export function formatDay(iso) {
  if (typeof iso !== "string" || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return null;
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
  });
}

function provenance(inv) {
  if (inv.provenance !== "renewal_cycle") return "";
  const when = inv.cycle_date ? ` (renewal date ${formatDay(inv.cycle_date)})` : "";
  return `From current renewal cycle${when}`;
}

const DASH = { primary: "—", secondary: null, tone: null, quiet: true };

/** What the INVOICED cell shows. */
export function invoicedCell(inv) {
  if (!inv) return { ...DASH, title: "Invoice state not available" };
  switch (inv.state) {
    case "invoiced": {
      const src = inv.source_label ? ` · Source: ${inv.source_label}` : "";
      const date = formatDay(inv.issued_on);
      if (date) {
        return { primary: date, secondary: inv.number || null, tone: null, quiet: false,
                 title: `Invoice issued ${date}${src}\n${provenance(inv)}` };
      }
      return { primary: "Invoiced",
               secondary: inv.number ? `${inv.number} · date not recorded` : "Date not recorded",
               tone: null, quiet: false,
               title: `Invoice issued — issue date not recorded${src}\n${provenance(inv)}` };
    }
    case "finops_draft":
      return { primary: "FinOps draft", secondary: inv.number || null, tone: null, quiet: false,
               title: "A draft invoice was created in FinOps from this proposal. "
                    + "Pulse cannot see whether FinOps approved or issued it." };
    case "unlinked":
      return { primary: "Not linked", secondary: null, tone: null, quiet: true,
               title: "Renewal proposal is not linked to a renewal specification, "
                    + "so its invoice state cannot be shown." };
    case "unavailable":
      return { primary: "Unavailable", secondary: null, tone: null, quiet: true,
               title: inv.provenance === "renewal_cycle"
                 ? "The client's renewal record has moved on to a later cycle; "
                   + "this cycle's invoice detail was not retained."
                 : "Invoice state could not be read for this proposal." };
    case "other_version":
      return { ...DASH, title: `Invoice state is shown on v${inv.anchor_version}` };
    case "none":
    default:
      return { ...DASH, title: inv.provenance === "renewal_cycle"
        ? "No invoice recorded for this renewal cycle"
        : "No invoice linked to this proposal in Pulse" };
  }
}

/** What the PAID cell shows. */
export function paidCell(inv) {
  if (inv?.state === "finops_draft") {
    return { primary: "Not tracked", secondary: null, tone: null, quiet: true,
             title: "Pulse does not record payment for FinOps invoices." };
  }
  if (inv?.state !== "invoiced") {
    return { ...DASH, title: "Not invoiced" };
  }
  const status = inv.payment_status || "awaiting_payment";
  const label = inv.payment_label || "Awaiting payment";
  const date = formatDay(inv.paid_on);
  return {
    primary: label,
    secondary: date && (status === "paid" || status === "part_paid") ? date : null,
    tone: toneClasses(PAYMENT_TONES[status] || "muted"),
    quiet: false,
    title: `${label}${date ? ` · ${date}` : ""}\n${provenance(inv)}`,
  };
}

/** Sort keys for the two columns (nulls sort last in `useSortable`). */
export function invoiceSortKeys(inv) {
  const invoiced = inv?.state === "invoiced";
  const draft = inv?.state === "finops_draft";
  return {
    // Known issue dates sort chronologically; "date not recorded" and
    // FinOps drafts sort ahead of them, un-invoiced rows last.
    invoice_sort: invoiced ? (inv.issued_on || "0000-00-00")
                  : draft ? "0000-00-00" : null,
    payment_sort: invoiced
      ? `${PAYMENT_RANK[inv.payment_status] || 1}|${inv.paid_on || ""}`
      : null,
  };
}

export const INVOICE_FILTERS = [
  { value: "", label: "All invoice states" },
  { value: "not_invoiced", label: "Not invoiced" },
  { value: "invoiced", label: "Invoiced" },
  { value: "awaiting_payment", label: "Awaiting payment" },
  { value: "paid", label: "Paid" },
];

/** Client-side invoice/payment filter. "Not invoiced" only matches rows
 *  whose linked record says so — not rows whose state is unknown. */
export function matchesInvoiceFilter(inv, filter) {
  if (!filter) return true;
  const state = inv?.state;
  switch (filter) {
    case "not_invoiced": return state === "none";
    case "invoiced": return state === "invoiced";
    case "awaiting_payment":
      return state === "invoiced"
        && ["awaiting_payment", "part_paid"].includes(inv.payment_status || "awaiting_payment");
    case "paid": return state === "invoiced" && inv.payment_status === "paid";
    default: return true;
  }
}
